import { KeyboardNavigationKey } from '../../../../util/keyboard';

const ItemKeyboardNavigationKeys = [
  KeyboardNavigationKey.ArrowDown,
  KeyboardNavigationKey.ArrowUp,
  KeyboardNavigationKey.End,
  KeyboardNavigationKey.Home,
] as const;

/**
 * Represents the type of keyboard key, {@link KeyboardNavigationKey}, that should trigger the
 * navigation of focused state between items in a list, when attached to a keyboard event,
 * {@link KeyboardEvent} that is fired from an 'onKeyDown' event handler.
 */
export type ItemNavigationKeyboardKey = (typeof ItemKeyboardNavigationKeys)[number];

export const isItemNavigationKeyboardKey = (key: string): key is ItemNavigationKeyboardKey =>
  ItemKeyboardNavigationKeys.includes(key as ItemNavigationKeyboardKey);

/**
 * Defines the direction in which focus state should shift between items in a list when keyboard
 * events {@link ItemNavigationKeyboardKey} are attached to the event included in an 'onKeyDown'
 * event handler.
 */
export enum ItemNavigationDirection {
  Forward = 'Forward',
  Reverse = 'Reverse',
}

export const isItemNavigationDirection = (value: unknown): value is ItemNavigationDirection =>
  Object.values(ItemNavigationDirection).includes(value as ItemNavigationDirection);

/**
 * Defines the starting point from which to begin searching for a focusable item in a list when
 * initially rendering in an open state.
 */
export enum ItemNavigationStartingPoint {
  Bottom = 'Bottom',
  Top = 'Top',
}

export const isItemNavigationStartingPoint = (
  value: unknown,
): value is ItemNavigationStartingPoint =>
  Object.values(ItemNavigationStartingPoint).includes(value as ItemNavigationStartingPoint);

/**
 * Maps a given {@link ItemNavigationDirection} or {@link ItemNavigationStartingPoint} to the
 * corresponding {@link ItemNavigationDirection}.  For instance, if the starting point for
 * navigation is the bottom of the list, {@link ItemNavigationStartingPoint.Bottom}, the direction
 * for navigation would be reverse, {@link ItemNavigationDirection.Reverse}.
 */
export const toItemNavigationDirection = (
  point: ItemNavigationDirection | ItemNavigationStartingPoint,
): ItemNavigationDirection => {
  if (isItemNavigationDirection(point)) {
    return point;
  }
  switch (point) {
    case ItemNavigationStartingPoint.Bottom:
      return ItemNavigationDirection.Reverse;
    case ItemNavigationStartingPoint.Top:
      return ItemNavigationDirection.Forward;
  }
};
